import { Dispatch, SetStateAction, useEffect, useState } from "react";

type useQueryParamOptions = {
  keepChangesInHistory?: boolean;
};

const useQueryParam = <T = unknown>(
  key: string,
  defaultValue?: T | (() => T),
  options: useQueryParamOptions = {}
): [T, Dispatch<SetStateAction<T>>] => {
  const [value, setValue] = useState<T>(defaultValue);

  // Try and set the value from the query params, otherwise fallback to given
  // default value.
  useEffect(() => {
    const url = new URL(window.location.href);

    // This is a bit of hack to force TypeScript to cast the value from the
    // search params into the given generic. Otherwise we an error telling us the
    // conversion is impossible. Adding the cast to unknown gets around this.
    // This allows us to use generics with the hook, and therefore type the
    // return value.
    let param: string | T = url.searchParams.get(key);

    // Handle param being an array
    if (param && param.split(",").length > 1) {
      param = param.split(",") as unknown as T;
    }

    setValue((param as unknown as T) || defaultValue);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When the query params change, sync it with the internal state value.
  useEffect(() => {
    const check = () => {
      const url = new URL(window.location.href);
      const param = url.searchParams.get(key) as unknown as T;

      setValue(param);
    };

    if (options.keepChangesInHistory) {
      window.addEventListener("popstate", check);
    }

    return () => window.removeEventListener("popstate", check);
  }, [options.keepChangesInHistory]); // eslint-disable-line react-hooks/exhaustive-deps

  // When the internal state value changes, sync it with the query params.
  useEffect(() => {
    const url = new URL(window.location.href);

    if (
      (Array.isArray(value) && value.length !== 0) ||
      (!Array.isArray(value) && value)
    ) {
      url.searchParams.set(key, String(value));
    } else if (url.searchParams.get(key) !== String(value)) {
      url.searchParams.delete(key);
    }

    if (url.href !== window.location.href) {
      options.keepChangesInHistory
        ? window.history.pushState({}, "", url.href)
        : window.history.replaceState({}, "", url.href);
    }
  }, [key, value, options.keepChangesInHistory]);

  return [value, setValue];
};

export default useQueryParam;
